import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { teamApi } from '@/lib/api/team/teamApi';
import type { Team } from 'dico-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { MonoIcon } from '@/components/ui/MonoIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { getPickerIcon, PICKER_ICON_NAMES } from '@/lib/picker-icons';

// Common IANA timezone options
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC - Coordinated Universal Time' },
  { value: 'America/New_York', label: 'GMT-5:00 - Eastern Time - New York' },
  { value: 'America/Chicago', label: 'GMT-6:00 - Central Time - Chicago' },
  { value: 'America/Denver', label: 'GMT-7:00 - Mountain Time - Denver' },
  { value: 'America/Los_Angeles', label: 'GMT-7:00 - Pacific Time - Los Angeles' },
  { value: 'America/Phoenix', label: 'GMT-7:00 - Mountain Time - Phoenix' },
  { value: 'Europe/London', label: 'GMT+0:00 - London' },
  { value: 'Europe/Paris', label: 'GMT+1:00 - Paris' },
  { value: 'Europe/Berlin', label: 'GMT+1:00 - Berlin' },
  { value: 'Asia/Tokyo', label: 'GMT+9:00 - Tokyo' },
  { value: 'Asia/Shanghai', label: 'GMT+8:00 - Shanghai' },
  { value: 'Asia/Singapore', label: 'GMT+8:00 - Singapore' },
  { value: 'Australia/Sydney', label: 'GMT+11:00 - Sydney' }
];

interface TeamSettingsPageProps {
  workspaceId: string;
  teamId: string;
}

export function TeamSettingsPage({ workspaceId, teamId }: TeamSettingsPageProps) {
  const [team, setTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [iconName, setIconName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [teamCount, setTeamCount] = useState<number | null>(null);

  useEffect(() => {
    const loadTeam = async () => {
      setIsLoading(true);
      try {
        const [teamRes, teamsRes] = await Promise.all([
          teamApi.getTeamById(workspaceId, teamId),
          teamApi.getTeams(workspaceId)
        ]);

        if (teamsRes.success && teamsRes.data) {
          setTeamCount(teamsRes.data.length);
        }

        if (teamRes.success && teamRes.data) {
          setTeam(teamRes.data);
          setName(teamRes.data.name);
          setIdentifier(teamRes.data.key);
          setIconName(teamRes.data.icon ?? '');
          setTimezone(teamRes.data.timezone ?? 'UTC');
        }
      } catch (error) {
        console.error('Failed to load team:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTeam();
  }, [workspaceId, teamId]);

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center text-sm">
        Loading...
      </div>
    );
  }

  if (!team) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center text-sm">
        Team not found.
      </div>
    );
  }

  const handleSave = async () => {
    if (!name.trim() || !identifier.trim()) return;

    // Validate identifier format (3-5 uppercase letters)
    const keyPattern = /^[A-Z]{3,5}$/;
    if (!keyPattern.test(identifier.trim())) {
      toast.error('Identifier must be 3-5 uppercase letters.');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await teamApi.updateTeam(workspaceId, teamId, {
        name: name.trim(),
        key: identifier.trim().toUpperCase(),
        icon: iconName.trim() || null,
        timezone: timezone
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (teamCount !== null && teamCount <= 1) {
      toast.error('You must have at least one team in a workspace.');
      return;
    }
    try {
      await teamApi.deleteTeam(workspaceId, teamId);
      window.location.href = `/workspace/${workspaceId}/settings/workspace`;
    } catch (error) {
      const message =
        error?.data?.error ||
        error?.data?.message ||
        (error instanceof Error ? error.message : null) ||
        'Failed to delete team.';
      toast.error(message);
    }
  };

  const isDirty =
    name.trim() !== team.name ||
    identifier.trim() !== team.key ||
    iconName.trim() !== (team.icon ?? '') ||
    timezone !== (team.timezone ?? 'UTC');

  const iconType = iconName ? getPickerIcon(iconName) : null;
  const canDelete = teamCount === null ? true : teamCount > 1;

  const teamIconItems = useMemo(() => {
    const record: Record<string, ReactNode> = {
      '': (
        <div className="flex items-center gap-2">
          <MonoIcon type="UserGroup" />
          <span>Default</span>
        </div>
      )
    };
    for (const name of PICKER_ICON_NAMES) {
      const pickerType = getPickerIcon(name) ?? 'Circle';
      record[name] = (
        <div className="flex items-center gap-2">
          <MonoIcon type={pickerType} />
          <span>{name}</span>
        </div>
      );
    }
    return record;
  }, []);

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Team Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Manage your team details and settings.
        </p>
      </div>

      <Separator />

      {/* Icon & Name */}
      <div className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-base font-medium">Icon & Name</h2>
          <div className="flex items-start gap-6">
            <div className="bg-primary/10 flex size-16 items-center justify-center rounded-xl">
              <MonoIcon type={iconType ?? 'UserGroup'} className="h-8 w-8 text-foreground" />
            </div>
            <div className="flex-1 space-y-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Team name"
                className="max-w-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Icon */}
      <div className="space-y-2">
        <Label htmlFor="team-icon">Icon (optional)</Label>
        <Select
          value={iconName}
          onValueChange={(value) => setIconName(value || '')}
          items={teamIconItems}
        >
          <SelectTrigger id="team-icon" className="max-w-sm">
            <SelectValue placeholder="Select an icon" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="">
              <div className="flex items-center gap-2">
                <MonoIcon type="UserGroup" />
                <span>Default</span>
              </div>
            </SelectItem>
            {PICKER_ICON_NAMES.map((name) => {
              const pickerType = getPickerIcon(name) ?? 'Circle';
              return (
                <SelectItem key={name} value={name}>
                  <div className="flex items-center gap-2">
                    <MonoIcon type={pickerType} />
                    <span>{name}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          Icons are stored as `MonoIcon` names and rendered consistently across the app.
        </p>
      </div>

      <Separator />

      {/* Identifier */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-medium">Identifier</h2>
          <p className="text-muted-foreground mt-1 text-sm">Used in issue IDs</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="team-identifier">Identifier</Label>
          <Input
            id="team-identifier"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value.toUpperCase())}
            placeholder="RAU"
            className="max-w-[120px] font-mono text-sm uppercase"
            maxLength={5}
          />
          <p className="text-muted-foreground text-xs">3-5 uppercase letters (e.g., ENG, PLAT, DESIGN)</p>
        </div>
      </div>

      <Separator />

      {/* Timezone */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-medium">Timezone</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            The timezone should be set as the location where most of your team members reside. All other
            times referenced by the team will be relative to this timezone setting. For example, if the team
            uses cycles, each cycle will start at midnight in the specified timezone.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="team-timezone">Timezone</Label>
          <Select
            value={timezone}
            onValueChange={setTimezone}
            items={TIMEZONE_OPTIONS.map((tz) => ({ value: tz.value, label: tz.label }))}
          >
            <SelectTrigger className="max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={!isDirty || isSaving} size="sm">
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
        {saveSuccess && <span className="text-muted-foreground text-sm">Saved.</span>}
      </div>

      <Separator />

      {/* Danger zone */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-medium">Danger zone</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Permanently delete this team and all of its data.
          </p>
          {!canDelete && (
            <p className="text-muted-foreground mt-1 text-sm">
              You can’t delete the only team in a workspace.
            </p>
          )}
        </div>
        <AlertDialog>
          <AlertDialogTrigger
            render={<Button variant="destructive" size="sm" disabled={!canDelete} />}
          >
            Delete team
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete team?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{team.name}</strong> and all of its data.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleDelete}
                disabled={!canDelete}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
